import React, { useEffect, useState} from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'

import MDEditor from '@uiw/react-md-editor'

import TagSelector from '../tags/TagSelector'
import TerminologySourcesEditor, { emptySource, validateSources } from '../shared/TerminologySourcesEditor'
import TerminologySubmissionThanks from '../shared/TerminologySubmissionThanks'
import { TerminologyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { createRevision } from '../../reducers/revisionReducer'
import { setErrorNotification } from '../../reducers/notificationReducer'

const stripSourceIds = (sources) => (sources || []).map(s => ({
    source: s.source || '',
    url: s.url || '',
    publication_data: s.publication_data || '',
    pub_date: s.pub_date || '',
}))

const TerminologyRevisionModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [open, setOpen] = useState(false)

    // when the user submits, we want to show a confimation message before closing
    const [showConfirmation, setShowConfirmation] = useState(false)

    // form state
    const [term, setTerm] = useState('')
    const [termError, setTermError] = useState(false)
    const [termHelper, setTermHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [sources, setSources] = useState([emptySource()])
    const [formTags, setFormTags] = useState([])
    const [hasChanges, setHasChanges] = useState(false)

    // init fields
    useEffect(() => {
        setTerm(props.term)
        setDescription(props.description)
        const initialSources = (props.sources && props.sources.length > 0)
            ? stripSourceIds(props.sources)
            : [emptySource()]
        setSources(initialSources)
        if (props.tags && props.tags.length > 0) {
            setFormTags(props.tags)
        }
    }, [props])

    useEffect(() => {
        const initialTags = (props.tags || []).slice().sort()
        const currentTags = (formTags || []).slice().sort()
        const tagsChanged = JSON.stringify(initialTags) !== JSON.stringify(currentTags)

        const initialSources = stripSourceIds(props.sources)
        const currentSources = stripSourceIds(sources)
        const sourcesChanged = JSON.stringify(initialSources) !== JSON.stringify(currentSources)

        const changed =
            (term || '') !== (props.term || '') ||
            (description || '') !== (props.description || '') ||
            sourcesChanged ||
            tagsChanged

        setHasChanges(changed)
    }, [
        term,
        description,
        sources,
        formTags,
        props.term,
        props.description,
        props.sources,
        props.tags,
    ])

    // basic form validation
    const validate = (field, setError, setHelper) => {
        if(!field || field.length === 0) {
            setError(true)
            setHelper('This field is required')
            return false
        } else {
            setError(false)
            setHelper('')
            return true
        }
    }

    const handleClickOpen = () => {
        setOpen(true)
        setShowConfirmation(false)
    }

    const handleClose = () => {
        if (showConfirmation) {
            setOpen(false)
        } else {
            if (window.confirm('Discard your suggestions?')) {
                setOpen(false)
            }
        }
    }

    const handleSubmit = () => {
        const sourcesCheck = validateSources(sources)
        if (
            validate(term, setTermError, setTermHelper) &&
            validate(description, setDescriptionError, setDescriptionHelper) &&
            sourcesCheck.ok
        ) {
            if (window.confirm('Submit your changes?')) {
                const data = {
                    terminology: props.id,
                    user: user.id,
                    term: term,
                    description: description,
                    tags: (formTags && formTags.length > 0) ? formTags : [],
                    sources: sources.map(s => ({
                        source: s.source,
                        url: s.url || null,
                        publication_data: s.publication_data || null,
                        pub_date: s.pub_date || null,
                    })),
                }

                dispatch(createRevision(data, user.staff || user.terminology_admin))
                setShowConfirmation(true)
            }
        } else if (!sourcesCheck.ok) {
            dispatch(setErrorNotification(sourcesCheck.message))
        }
    }

    return (
        <div>
            { user && !user.anon ?
                <Button
                    {...TerminologyModalStyle.viewButton.props}
                    sx={TerminologyModalStyle.viewButton.style}
                    onClick={()=>handleClickOpen()}>
                    Suggest Changes
                </Button>
            :
                <Button
                    {...TerminologyModalStyle.viewButton.props}
                    sx={TerminologyModalStyle.viewButton.style}
                    disabled
                >
                    Log in to Suggest Changes
                </Button>
            }
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`terminology-modal-${props.id}`}
                open={open}
                fullWidth
                {...TerminologyModalStyle.modal}
            >
                <BootstrapDialogTitle id={`terminology-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={TerminologyModalStyle.headingContainer}>
                        <MenuBookIcon sx={TerminologyModalStyle.icon}/>
                        {showConfirmation ? "Thank you for your submission" : "Suggest Changes"}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    {showConfirmation ? (
                        <TerminologySubmissionThanks />
                    ) : (
                        <>
                            <Box sx={TerminologyModalStyle.content}>
                                <Box sx={TerminologyModalStyle.dateContainer}>
                                    <Box sx={TerminologyModalStyle.dates}>
                                        <Typography {...TerminologyModalStyle.heading}>
                                            Last Updated:&nbsp;
                                        </Typography>
                                        <Typography {...TerminologyModalStyle.text}>
                                            {props.updated.split('T')[0]}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={TerminologyModalStyle.mobileContainer}>
                                <Box sx={TerminologyModalStyle.mobileDate}>
                                    <Typography gutterBottom {...TerminologyModalStyle.dateHeading}>
                                        Last Updated:&nbsp;
                                    </Typography>
                                    <Typography gutterBottom {...TerminologyModalStyle.text}>
                                        {props.updated.split('T')[0]}
                                    </Typography>
                                </Box>
                            </Box>
                            <br />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={term}
                                        onChange={e => setTerm(e.target.value)}
                                        label="Term"
                                        required
                                        error={termError}
                                        helperText={termHelper}
                                        onBlur={()=>validate(term, setTermError, setTermHelper)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <FormLabel component="legend">Description (Markdown supported)</FormLabel>
                                        <Box sx={{ marginTop: '10px' }}>
                                            <MDEditor
                                                value={description}
                                                onChange={setDescription}
                                                preview="edit"
                                                hideToolbar={false}
                                                data-color-mode="light"
                                            />
                                        </Box>
                                        {descriptionError && (
                                            <Typography color="error" variant="caption">
                                                {descriptionHelper}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TerminologySourcesEditor sources={sources} onChange={setSources} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TagSelector
                                        tagType="default"
                                        selectedTags={formTags}
                                        onChange={setFormTags}
                                        multiple={true}
                                        label="Content Keywords"
                                        maxTags={5}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {showConfirmation ? (
                        <Button autoFocus onClick={handleClose}>
                            Ok
                        </Button>
                    ) : (
                        <>
                            <Button autoFocus onClick={handleClose}>
                                Discard
                            </Button>
                            {!hasChanges && (
                                <Typography
                                    sx={(theme) => ({
                                        ml: 2,
                                        color: theme.palette.text.secondary,
                                        textTransform: 'none',
                                        ...theme.typography.button,
                                    })}
                                >
                                    Make at least one change to submit.
                                </Typography>
                            )}
                            <Button autoFocus onClick={handleSubmit} disabled={!hasChanges}>
                                Submit
                            </Button>
                        </>
                    )}
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default TerminologyRevisionModal
